import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTrackCaptionGenerationFailed } from '@air/analytics';

import { isCaptionVideoTask } from '~/components/CaptionVideoTasks/isCaptionVideoTask';
import { useCaptionVideoTaskGalleryUpdate } from '~/components/CaptionVideoTasks/useCaptionVideoTaskGalleryUpdate';
import { useVideoClipTaskGalleryUpdate } from '~/components/VideoClipTasks/useVideoClipTaskGalleryUpdate';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { makeAssetTransformationTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';
import { type SharedSyncTasksParams } from '~/utils/taskUtils/useSharedSyncTasks';

type OnCompleteParams = Parameters<
  NonNullable<SharedSyncTasksParams<'AssetTransformation', 'AssetTransformer'>['onComplete']>
>[0];

type OnErrorParams = Parameters<
  NonNullable<SharedSyncTasksParams<'AssetTransformation', 'AssetTransformer'>['onError']>
>[0];

export const usePrivateAssetTransformationTasksSync = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { onVideoClipTaskComplete } = useVideoClipTaskGalleryUpdate();
  const { onCaptionVideoTaskComplete } = useCaptionVideoTaskGalleryUpdate();
  const { trackCaptionGenerationFailed } = useTrackCaptionGenerationFailed();

  const tasksSelector = useMemo(
    () => makeAssetTransformationTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );

  const onComplete = useCallback(
    async (params: OnCompleteParams) => {
      await onVideoClipTaskComplete(params);
      await onCaptionVideoTaskComplete(params);
    },
    [onVideoClipTaskComplete, onCaptionVideoTaskComplete],
  );

  const onError = useCallback(
    ({ localTask }: OnErrorParams) => {
      if (!isCaptionVideoTask(localTask)) {
        return;
      }

      trackCaptionGenerationFailed({
        video_id: localTask.metadata.assetVersionId,
        error_type: 'task_failed',
      });
    },
    [trackCaptionGenerationFailed],
  );

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'AssetTransformation',
    remoteType: 'AssetTransformer',
    onComplete,
    onError,
  });

  const syncExecutedForReadyStateRef = useRef(false);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);
};
